import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ShieldAlertIcon, BugIcon, PackageOpenIcon, ActivityIcon, FlameIcon, DatabaseIcon, RefreshCwIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteBeveiligingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website beveiliging | Bescherm je website | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Website beveiliging: bescherm je website tegen hackers en malware. Tips over SSL, updates, sterke wachtwoorden en meer. Praktisch advies van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website beveiliging | Bescherm je website | Noord',
      'og:description': 'Website beveiliging: bescherm je website tegen hackers en malware. Tips over SSL, updates, sterke wachtwoorden en meer.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-beveiliging',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe beveilig je een website?',
    a: 'Een website beveiligen begint met een SSL-certificaat, sterke wachtwoorden en regelmatige updates van je CMS en plugins. Daarnaast is het belangrijk om een firewall in te stellen, dagelijkse backups te maken en verdachte activiteiten te monitoren. Wij nemen al deze stappen standaard mee in onze website beveiliging service.'
  }, {
    q: 'Is WordPress veilig?',
    a: "WordPress is van zichzelf een veilig platform, mits het goed wordt onderhouden. De meeste beveiligingsproblemen ontstaan door verouderde plugins, zwakke wachtwoorden of onbetrouwbare thema's. Met professionele WordPress beveiliging, regelmatige updates en monitoring is WordPress een uitstekende en veilige keuze voor je bedrijfswebsite."
  }, {
    q: 'Hoe vaak moeten beveiligingsupdates worden uitgevoerd?',
    a: "Kritieke beveiligingsupdates voeren wij direct door zodra ze beschikbaar zijn. Reguliere updates van WordPress core, thema's en plugins worden wekelijks gecontroleerd en maandelijks doorgevoerd na een grondige test. Zo blijft je website altijd beschermd tegen de nieuwste dreigingen."
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1865">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1866">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1867">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1868" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1869" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1870" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1871">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-1872">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-1873">
              Home
            </a>
            <span className="text-navy-500" data-id="element-1874">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-1875">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-1876">/</span>
            <span className="text-accent-light font-medium" data-id="element-1877">
              Website beveiliging
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-1878">
            Website beveiliging voor bedrijven
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-1879">
            Bescherm je website tegen hacks en malware. Wij zorgen voor
            professionele website security zodat jij zorgeloos kunt ondernemen.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-1880">
            <a href="#beveiliging-info" onClick={e => scrollToSection(e, 'beveiliging-info')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1881">
              Bekijk beveiliging
              <ArrowRightIcon className="w-5 h-5" data-id="element-1882" />
            </a>
          </div>
        </div>
      </section>

      {/* Belangrijkste risico's */}
      <section id="beveiliging-info" className="py-24 bg-surface-muted" data-id="element-1883">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1884">
          <div className="text-center mb-16" data-id="element-1885">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1886">
              Belangrijkste risico's zonder website beveiliging
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1887">
              Zonder goede beveiliging is je website kwetsbaar voor aanvallen
              die je bedrijf ernstig kunnen schaden.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1888">
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1889">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-6" data-id="element-1890">
                <ShieldAlertIcon className="w-6 h-6 text-red-500" data-id="element-1891" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1892">Hacks</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1893">
                Hackers kunnen toegang krijgen tot je website, gevoelige
                gegevens stelen of je site gebruiken voor kwaadaardige
                doeleinden. Dit schaadt je reputatie en het vertrouwen van je
                klanten.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1894">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-6" data-id="element-1895">
                <BugIcon className="w-6 h-6 text-red-500" data-id="element-1896" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1897">Malware</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1898">
                Malware kan ongemerkt op je website worden geplaatst en
                bezoekers infecteren. Google kan je website vervolgens
                blokkeren, waardoor je vindbaarheid volledig wegvalt.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1899">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-6" data-id="element-1900">
                <PackageOpenIcon className="w-6 h-6 text-red-500" data-id="element-1901" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1902">
                Verouderde plugins
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1903">
                Verouderde WordPress plugins en thema's bevatten bekende
                beveiligingslekken. Zonder regelmatige updates is je website een
                makkelijk doelwit voor aanvallers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hoe wij beveiligen */}
      <section className="py-24 bg-white" data-id="element-1904">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1905">
          <div className="text-center mb-16" data-id="element-1906">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1907">
              Hoe wij jouw website beveiligen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1908">
              Onze website beveiliging aanpak beschermt je site op meerdere
              niveaus tegen alle bekende dreigingen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1909">
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1910">
              <div className="flex items-start gap-4" data-id="element-1911">
                <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-1912">
                  <ActivityIcon className="w-6 h-6 text-accent" data-id="element-1913" />
                </div>
                <div data-id="element-1914">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1915">
                    Security monitoring
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-1916">
                    Wij monitoren je website 24/7 op verdachte activiteiten,
                    ongeautoriseerde inlogpogingen en bekende kwetsbaarheden.
                    Bij een dreiging grijpen wij direct in.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1917">
              <div className="flex items-start gap-4" data-id="element-1918">
                <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-1919">
                  <FlameIcon className="w-6 h-6 text-accent" data-id="element-1920" />
                </div>
                <div data-id="element-1921">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1922">
                    Firewall
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-1923">
                    Een professionele web application firewall blokkeert
                    kwaadaardig verkeer voordat het je website bereikt.
                    Bescherming tegen brute force aanvallen, SQL-injecties en
                    meer.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1924">
              <div className="flex items-start gap-4" data-id="element-1925">
                <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-1926">
                  <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-1927" />
                </div>
                <div data-id="element-1928">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1929">
                    Backups
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-1930">
                    Dagelijkse automatische backups van je volledige website en
                    database. Mocht er iets misgaan, dan herstellen wij je
                    website snel naar een werkende versie.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1931">
              <div className="flex items-start gap-4" data-id="element-1932">
                <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-1933">
                  <RefreshCwIcon className="w-6 h-6 text-accent" data-id="element-1934" />
                </div>
                <div data-id="element-1935">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1936">
                    Updates
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-1937">
                    Regelmatige updates van WordPress core, plugins en thema's.
                    Wij testen elke update grondig voordat deze live gaat om
                    compatibiliteitsproblemen te voorkomen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-1938">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1939">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-1940">
            Gerelateerde pagina's:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1941">
            <a href="#/website-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1942">
              Website onderhoud
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1943">•</span>
            <a href="#/wordpress-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1944">
              WordPress onderhoud
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1945">•</span>
            <a href="#/website-hosting" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1946">
              Website hosting
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-1947">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1948">
          <div className="text-center mb-16" data-id="element-1949">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1950">
              Veelgestelde vragen over website beveiliging
            </h2>
          </div>

          <div className="space-y-4" data-id="element-1951">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-1952">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-1953">
                  <span className="font-bold text-navy-900" data-id="element-1954">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1955" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-1956" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-1957">
                    {faq.a}
                  </div>}
              </div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-1958">
            <a href="#contact" onClick={e => scrollToSection(e, 'contact')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-semibold px-6 py-3 rounded-button shadow-soft hover:shadow-medium transition-all duration-200 group" data-id="element-1959">
              Nog vragen? Neem contact op
              <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" data-id="element-1960" />
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-1961" />
    </main>;
}